import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../shared-utils/src';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  className?: string;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  optionsListClassName?: string;
  selectedOptionClassName?: string;
  selectedOptionContainerClassName?: string;
  onSelect?: (option: SelectOption) => void;
  maxHeight?: number;
  chevronIcon?: React.ReactNode;
}

const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: {
      duration: 0.15,
    },
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: {
      duration: 0.15,
    },
  },
};

const optionVariants = {
  hidden: {
    opacity: 0,
    y: -15,
    scale: 0.95,
  },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: index * 0.04,
      duration: 0.25,
      ease: 'easeOut',
    },
  }),
};

const chevronVariants = {
  closed: { rotate: 0 },
  open: { rotate: 180 },
};

const Select: React.FC<SelectProps> = ({
  options,
  value,
  placeholder = 'Select an option...',
  disabled = false,
  className = '',
  optionsListClassName = '',
  onSelect,
  maxHeight = 200,
  selectedOptionClassName = '',
  selectedOptionContainerClassName = '',
  chevronIcon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const selectedOption = value
    ? options.find((opt) => opt.value === value) || null
    : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionSelect = (option: SelectOption) => {
    if (!option.disabled) {
      setIsOpen(false);
      onSelect?.(option);
    }
  };

  return (
    <div
      ref={selectRef}
      className={cn('relative inline-block w-full', className)}
    >
      <motion.div
        className={cn(
          `
          flex items-center justify-between w-full px-4 py-3 bg-white cursor-pointer
          transition-all duration-200 ease-in-out
          ${'h-full'}
        `,
          selectedOptionContainerClassName,
          {
            'bg-gray-100 cursor-not-allowed': disabled,
          },
        )}
        onClick={handleToggle}
      >
        <span
          className={cn(
            selectedOption ? 'text-gray-900' : 'text-gray-500',
            selectedOptionClassName,
          )}
        >
          {selectedOption?.label || placeholder}
        </span>

        {chevronIcon ? (
          chevronIcon
        ) : (
          <motion.svg
            className={`w-5 h-5 ${
              disabled ? 'text-gray-400' : 'text-gray-600'
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            variants={chevronVariants}
            animate={isOpen ? 'open' : 'closed'}
            transition={{ duration: 0.2 }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </motion.svg>
        )}
      </motion.div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute z-10 w-full mt-1 bg-white border border-gray-200 shadow-lg overflow-hidden"
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div
              className={cn(
                'overflow-y-auto text-gray-400',
                optionsListClassName,
              )}
              style={{ maxHeight: `${maxHeight}px` }}
            >
              {options.map((option, index) => (
                <motion.div
                  key={option.value}
                  className={cn(
                    'px-4 py-3 cursor-pointer',
                    {
                      'cursor-not-allowed bg-gray-50': option.disabled,
                    },
                    option.className,
                  )}
                  variants={optionVariants}
                  initial="hidden"
                  animate="visible"
                  custom={index}
                  onClick={() => handleOptionSelect(option)}
                >
                  {option.label}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export { Select };
